import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';

import { embeddingModelApi, llmProviderApi } from '../../services/api';
import type { EmbeddingModel, EmbeddingModelCreate } from '../../types';

interface EmbeddingModelFormProps {
  open: boolean;
  onClose: () => void;
  model?: EmbeddingModel;
}

export const EmbeddingModelForm: React.FC<EmbeddingModelFormProps> = ({
  open,
  onClose,
  model,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [formData, setFormData] = useState<EmbeddingModelCreate>({
    provider: 0,
    name: '',
    model: '',
    dimensions: 1536,
    cost_per_million_tokens: 0,
    is_active: true,
    config: {},
  });

  const { data: providersData } = useQuery({
    queryKey: ['llm-providers'],
    queryFn: () => llmProviderApi.list({}),
  });

  useEffect(() => {
    if (model) {
      setFormData({
        provider: model.provider,
        name: model.name,
        model: model.model,
        dimensions: model.dimensions,
        cost_per_million_tokens: model.cost_per_million_tokens || 0,
        is_active: model.is_active,
        config: model.config || {},
      });
    } else {
      setFormData({
        provider: 0,
        name: '',
        model: '',
        dimensions: 1536,
        cost_per_million_tokens: 0,
        is_active: true,
        config: {},
      });
    }
    setError(null);
  }, [model, open]);

  const createMutation = useMutation({
    mutationFn: (data: EmbeddingModelCreate) => embeddingModelApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['embedding-models'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create embedding model');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: EmbeddingModelCreate) => embeddingModelApi.update(model!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['embedding-models'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to update embedding model');
    },
  });

  const handleChange = (field: keyof EmbeddingModelCreate, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!formData.provider) {
      setError('Please select a provider');
      return;
    }

    if (!formData.dimensions || formData.dimensions <= 0) {
      setError('Dimensions must be greater than zero');
      return;
    }

    if (model) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isFullscreen ? false : 'md'}
      fullWidth
      fullScreen={isMobile || isFullscreen}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ py: isMobile ? 1.5 : 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
              {model ? 'Edit Embedding Model' : 'Add Embedding Model'}
            </Typography>
            {!isMobile && (
              <IconButton onClick={() => setIsFullscreen(!isFullscreen)} size="small">
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                label="Provider"
                value={formData.provider}
                onChange={(event) => handleChange('provider', Number(event.target.value))}
                required
                disabled={isLoading}
                InputProps={{ style: { fontSize: 16 } }}
              >
                <MenuItem value={0}>Select Provider</MenuItem>
                {providersData?.results.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Model Name"
                value={formData.name}
                onChange={(event) => handleChange('name', event.target.value)}
                required
                disabled={isLoading}
                helperText="Display name for the embedding model"
                InputProps={{ style: { fontSize: 16 } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Model ID"
                value={formData.model}
                onChange={(event) => handleChange('model', event.target.value)}
                required
                disabled={isLoading}
                helperText="API model identifier (for example text-embedding-3-small)"
                InputProps={{ style: { fontSize: 16 } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Dimensions"
                value={formData.dimensions}
                onChange={(event) => handleChange('dimensions', parseInt(event.target.value, 10) || 0)}
                required
                disabled={isLoading}
                inputProps={{ step: 1, min: 1 }}
                helperText="Embedding vector dimensions"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Cost per 1M tokens"
                value={formData.cost_per_million_tokens}
                onChange={(event) => handleChange('cost_per_million_tokens', parseFloat(event.target.value) || 0)}
                disabled={isLoading}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="Cost in USD per 1M tokens"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formData.is_active}
                    onChange={(event) => handleChange('is_active', event.target.checked)}
                    disabled={isLoading}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: isMobile ? 2 : 3,
            py: isMobile ? 1.5 : 2,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0,
            '& > button': { width: isMobile ? '100%' : 'auto' },
          }}
        >
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {model ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EmbeddingModelForm;